// Social Media Service
import { supabase } from '../../../lib/supabaseClient';
import { 
  SocialPost, 
  SocialAccount, 
  SocialStats, 
  PublishRequest, 
  PublishResponse,
  SocialPlatform 
} from '../types/social.types';

export class SocialMediaService {
  // Zarządzanie postami
  async createPost(post: Omit<SocialPost, 'id' | 'created_at'>): Promise<SocialPost> {
    const { data, error } = await supabase
      .from('social_posts')
      .insert([post])
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async updatePost(id: string, updates: Partial<SocialPost>): Promise<SocialPost> {
    const { data, error } = await supabase
      .from('social_posts')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async getPosts(filters?: {
    status?: string;
    platform?: SocialPlatform;
    dateFrom?: Date;
    dateTo?: Date;
  }): Promise<SocialPost[]> {
    let query = supabase.from('social_posts').select('*');

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }
    if (filters?.platform) {
      query = query.contains('platforms', [filters.platform]);
    }
    if (filters?.dateFrom) {
      query = query.gte('created_at', filters.dateFrom.toISOString());
    }
    if (filters?.dateTo) {
      query = query.lte('created_at', filters.dateTo.toISOString());
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  }

  async getPost(id: string): Promise<SocialPost> {
    const { data, error } = await supabase
      .from('social_posts')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  }

  async deletePost(id: string): Promise<void> {
    const { error } = await supabase
      .from('social_posts')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  // Zarządzanie kontami social media
  async getAccounts(): Promise<SocialAccount[]> {
    const { data, error } = await supabase
      .from('social_accounts')
      .select('*')
      .eq('active', true);

    if (error) throw error;
    return data;
  }

  async addAccount(account: Omit<SocialAccount, 'id'>): Promise<SocialAccount> {
    const { data, error } = await supabase
      .from('social_accounts')
      .insert([account])
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async updateAccount(id: string, updates: Partial<SocialAccount>): Promise<SocialAccount> {
    const { data, error } = await supabase
      .from('social_accounts')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async removeAccount(id: string): Promise<void> {
    const { error } = await supabase
      .from('social_accounts')
      .update({ active: false })
      .eq('id', id);

    if (error) throw error;
  }

  // Publikowanie postów - UŻYWA PRAWDZIWYCH API PLATFORM!
  async publishPost(request: PublishRequest): Promise<PublishResponse> {
    const post = await this.getPost(request.post_id);
    
    // Import API serwisu
    const { socialMediaAPI } = await import('./platformAPIs');
    
    // Publikuj na wszystkich platformach używając prawdziwych API
    const publishResults = await socialMediaAPI.publishToAll(
      post.content,
      request.platforms,
      post.images?.[0], // Pierwszy obraz jeśli istnieje
      post.scheduled_at ? new Date(post.scheduled_at) : undefined
    );
    
    // Konwertuj wyniki do formatu PublishResponse
    const results = Object.entries(publishResults).map(([platform, result]) => ({
      platform: platform as any,
      success: result.success,
      post_id: result.id || `${platform}_${Date.now()}`,
      error: result.error
    }));

    // Aktualizuj status posta
    if (results.every(r => r.success)) {
      await this.updatePost(request.post_id, {
        status: 'published',
        published_at: new Date()
      });
    }

    return {
      success: results.every(r => r.success),
      platform_results: results
    };
  }

  // Usunięte - używamy prawdziwych API z platformAPIs.ts

  // Harmonogramowanie postów
  async schedulePost(postId: string, scheduleTime: Date): Promise<void> {
    await this.updatePost(postId, {
      status: 'scheduled',
      scheduled_at: scheduleTime
    });
  }

  async getScheduledPosts(): Promise<SocialPost[]> {
    const { data, error } = await supabase
      .from('social_posts')
      .select('*')
      .eq('status', 'scheduled')
      .lte('scheduled_at', new Date().toISOString())
      .order('scheduled_at', { ascending: true });

    if (error) throw error;
    return data;
  }

  // Statystyki
  async getPostStats(postId: string): Promise<SocialStats[]> {
    const { data, error } = await supabase
      .from('social_stats')
      .select('*')
      .eq('post_id', postId);

    if (error) throw error;
    return data;
  }

  async updateStats(stats: Omit<SocialStats, 'id'>): Promise<SocialStats> {
    const { data: existing } = await supabase
      .from('social_stats')
      .select('*')
      .eq('post_id', stats.post_id)
      .eq('platform', stats.platform)
      .single();

    if (existing) {
      const { data, error } = await supabase
        .from('social_stats')
        .update(stats)
        .eq('id', existing.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } else {
      const { data, error } = await supabase
        .from('social_stats')
        .insert([stats])
        .select()
        .single();

      if (error) throw error;
      return data;
    }
  }

  async getAggregatedStats(dateFrom: Date, dateTo: Date): Promise<{
    totalPosts: number;
    totalReach: number;
    totalEngagement: number;
    avgEngagementRate: number;
    topPerformingPosts: SocialPost[];
  }> {
    const posts = await this.getPosts({ dateFrom, dateTo });
    const postIds = posts.map(p => p.id);

    const { data: stats, error } = await supabase
      .from('social_stats')
      .select('*')
      .in('post_id', postIds);

    if (error) throw error;

    const totalReach = stats.reduce((sum, stat) => sum + stat.reach, 0);
    const totalEngagement = stats.reduce((sum, stat) => sum + stat.likes + stat.shares + stat.comments, 0);
    const avgEngagementRate = totalReach > 0 ? (totalEngagement / totalReach) * 100 : 0;

    // Znajdź najlepsze posty
    const postEngagement = new Map<string, number>();
    stats.forEach(stat => {
      const current = postEngagement.get(stat.post_id) || 0;
      postEngagement.set(stat.post_id, current + stat.likes + stat.shares + stat.comments);
    });

    const topPostIds = Array.from(postEngagement.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([postId]) => postId);

    const topPerformingPosts = posts.filter(p => topPostIds.includes(p.id));

    return {
      totalPosts: posts.length,
      totalReach,
      totalEngagement,
      avgEngagementRate: Math.round(avgEngagementRate * 100) / 100,
      topPerformingPosts
    };
  }

  // Szablony i pomysły na posty
  async getContentCalendar(month: number, year: number): Promise<{
    date: Date;
    posts: SocialPost[];
    suggestions: string[];
  }[]> {
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0);
    
    const posts = await this.getPosts({
      dateFrom: startDate,
      dateTo: endDate
    });

    const calendar: { date: Date; posts: SocialPost[]; suggestions: string[] }[] = [];
    
    for (let day = 1; day <= endDate.getDate(); day++) {
      const date = new Date(year, month - 1, day);
      const dayPosts = posts.filter(p => {
        const postDate = new Date(p.scheduled_at || p.created_at);
        return postDate.getDate() === day;
      });

      const suggestions = this.generateDailySuggestions(date);
      
      calendar.push({
        date,
        posts: dayPosts,
        suggestions
      });
    }

    return calendar;
  }

  private generateDailySuggestions(date: Date): string[] {
    const suggestions: string[] = [];
    const dayOfWeek = date.getDay();
    const dayOfMonth = date.getDate();

    // Sugestie na podstawie dnia tygodnia
    switch (dayOfWeek) {
      case 1: // Poniedziałek
        suggestions.push('Motywacja na początek tygodnia');
        break;
      case 3: // Środa
        suggestions.push('Środa z wiedzą - post edukacyjny');
        break;
      case 5: // Piątek
        suggestions.push('Podsumowanie tygodnia');
        break;
    }

    // Sugestie na podstawie dnia miesiąca
    if (dayOfMonth === 1) {
      suggestions.push('Powitanie nowego miesiąca');
    }
    if (dayOfMonth === 15) {
      suggestions.push('Połowa miesiąca - czas na promocję');
    }

    // Zawsze dodaj uniwersalne sugestie
    suggestions.push(
      'Prezentacja produktu',
      'Historia sukcesu klienta',
      'Kulisy produkcji'
    );

    return suggestions;
  }
}

export const socialMediaService = new SocialMediaService();
